import { useState, useEffect, useRef } from "react";
import { apiClient } from "@/App";
import { toast } from "sonner";
import { Image, Upload, Trash2, X, Loader2, FolderOpen, AlertCircle, Link2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const categories = [
  { value: "general", label: "General" },
  { value: "site_progress", label: "Site Progress" },
  { value: "before_after", label: "Before/After" },
  { value: "completion", label: "Completion" },
  { value: "design", label: "Design" }
];

export default function PhotoGallery({ projectId }) {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [filter, setFilter] = useState("all");
  const [uploadMode, setUploadMode] = useState("file"); // "file" or "url"
  const [cloudinaryConfigured, setCloudinaryConfigured] = useState(false);
  const fileInputRef = useRef(null);
  const [newPhoto, setNewPhoto] = useState({
    image_url: "",
    caption: "",
    category: "general",
    file: null
  });

  useEffect(() => {
    fetchPhotos();
    checkCloudinaryStatus();
  }, [projectId, filter]);

  const checkCloudinaryStatus = async () => {
    try {
      const response = await apiClient.get("/cloudinary/status");
      setCloudinaryConfigured(response.data.configured);
    } catch (error) {
      setCloudinaryConfigured(false);
    }
  };

  const fetchPhotos = async () => {
    try {
      const url = filter === "all" 
        ? `/projects/${projectId}/photos`
        : `/projects/${projectId}/photos?category=${filter}`;
      const response = await apiClient.get(url);
      setPhotos(response.data);
    } catch (error) {
      console.error("Error fetching photos:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ["image/jpeg", "image/png", "image/gif", "image/webp", "image/heic", "image/heif"];
      if (!allowedTypes.includes(file.type)) {
        toast.error("Please select an image file (JPEG, PNG, GIF, WebP)");
        return;
      }
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error("File size must be less than 10MB");
        return;
      }
      setNewPhoto({ ...newPhoto, file });
    }
  };

  const handleUpload = async () => {
    if (uploadMode === "file") {
      if (!newPhoto.file) {
        toast.error("Please select a file to upload");
        return;
      }
      if (!cloudinaryConfigured) {
        toast.error("File upload is not configured. Please use URL mode or contact admin.");
        return;
      }
    } else {
      if (!newPhoto.image_url) {
        toast.error("Please enter an image URL");
        return;
      }
    }
    
    setUploading(true);
    try {
      if (uploadMode === "file") {
        // Upload file
        const formData = new FormData();
        formData.append("file", newPhoto.file);
        formData.append("project_id", projectId);
        formData.append("caption", newPhoto.caption || "");
        formData.append("category", newPhoto.category);
        
        await apiClient.post("/upload/photo", formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
        toast.success("Photo uploaded successfully");
      } else {
        // Upload via URL
        await apiClient.post(`/projects/${projectId}/photos`, {
          project_id: projectId,
          image_url: newPhoto.image_url,
          caption: newPhoto.caption,
          category: newPhoto.category
        });
        toast.success("Photo added successfully");
      }
      
      setNewPhoto({ image_url: "", caption: "", category: "general", file: null });
      if (fileInputRef.current) fileInputRef.current.value = "";
      setShowUpload(false);
      fetchPhotos();
    } catch (error) {
      console.error("Upload error:", error);
      toast.error(error.response?.data?.detail || "Failed to upload photo");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (photoId) => {
    if (!confirm("Delete this photo?")) return;
    try {
      await apiClient.delete(`/projects/${projectId}/photos/${photoId}`);
      toast.success("Photo deleted");
      setSelectedPhoto(null);
      fetchPhotos();
    } catch (error) {
      toast.error("Failed to delete photo");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="photo-gallery">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-[140px] bg-[#1A1A1A] border-[#262626] text-sm">
              <SelectValue placeholder="Filter" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Photos</SelectItem>
              {categories.map(cat => (
                <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="text-xs text-gray-500">{photos.length} photos</span>
        </div>
        <Button 
          size="sm" 
          className="bg-[#FF4500] hover:bg-[#E65100]"
          onClick={() => setShowUpload(true)}
          data-testid="add-photo-btn"
        >
          <Upload className="w-4 h-4 mr-1" />
          Add Photo
        </Button>
      </div>

      {/* Gallery Grid */}
      {photos.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-gray-500">
          <FolderOpen className="w-12 h-12 mb-3 opacity-50" />
          <p className="text-sm">No photos yet</p>
          <p className="text-xs mt-1">Upload site progress photos here</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {photos.map((photo) => (
            <div 
              key={photo.photo_id}
              className="relative aspect-square rounded-lg overflow-hidden bg-[#1A1A1A] cursor-pointer group"
              onClick={() => setSelectedPhoto(photo)}
            >
              <img 
                src={photo.image_url} 
                alt={photo.caption || "Project photo"} 
                className="w-full h-full object-cover transition-transform group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="absolute bottom-2 left-2 right-2">
                  <span className="text-[10px] bg-[#FF4500] px-1.5 py-0.5 rounded text-white">{photo.category}</span>
                  {photo.caption && (
                    <p className="text-xs text-white mt-1 truncate">{photo.caption}</p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Upload Modal */}
      {showUpload && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-[#1A1A1A] border border-[#262626] rounded-lg p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Add Photo</h3>
              <button onClick={() => setShowUpload(false)} className="text-gray-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Upload Mode Toggle */}
            <div className="flex gap-2 mb-4">
              <button
                onClick={() => setUploadMode("file")}
                className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-all ${
                  uploadMode === "file" 
                    ? "bg-[#FF4500] text-white" 
                    : "bg-[#262626] text-gray-400 hover:text-white"
                }`}
              >
                <Upload className="w-4 h-4" />
                Upload File
              </button>
              <button
                onClick={() => setUploadMode("url")}
                className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-all ${
                  uploadMode === "url" 
                    ? "bg-[#FF4500] text-white" 
                    : "bg-[#262626] text-gray-400 hover:text-white"
                }`}
              >
                <Link2 className="w-4 h-4" />
                Paste URL
              </button>
            </div>
            
            {/* Cloudinary Warning */}
            {uploadMode === "file" && !cloudinaryConfigured && (
              <div className="mb-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                <div className="text-xs text-amber-400">
                  <p className="font-medium">File upload not configured</p>
                  <p className="mt-1 text-amber-500/80">Add Cloudinary credentials in Settings to enable file uploads. Use URL mode for now.</p>
                </div>
              </div>
            )}
            
            <div className="space-y-4">
              {uploadMode === "file" ? (
                <div>
                  <Label className="text-xs text-gray-400">Select Image</Label>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp,image/heic,image/heif"
                    onChange={handleFileSelect}
                    className="w-full mt-1 text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-[#FF4500] file:text-white hover:file:bg-[#E65100] file:cursor-pointer"
                    data-testid="photo-file-input"
                  />
                  {newPhoto.file && (
                    <p className="text-xs text-green-400 mt-2">
                      Selected: {newPhoto.file.name} ({(newPhoto.file.size / 1024 / 1024).toFixed(2)} MB)
                    </p>
                  )}
                </div>
              ) : (
                <div>
                  <Label className="text-xs text-gray-400">Image URL</Label>
                  <Input
                    value={newPhoto.image_url}
                    onChange={(e) => setNewPhoto({ ...newPhoto, image_url: e.target.value })}
                    placeholder="https://..."
                    className="bg-[#171717] border-[#262626]"
                    data-testid="photo-url-input"
                  />
                </div>
              )}
              
              <div>
                <Label className="text-xs text-gray-400">Caption (optional)</Label>
                <Input
                  value={newPhoto.caption}
                  onChange={(e) => setNewPhoto({ ...newPhoto, caption: e.target.value })}
                  placeholder="Describe this photo"
                  className="bg-[#171717] border-[#262626]"
                />
              </div>
              
              <div>
                <Label className="text-xs text-gray-400">Category</Label>
                <Select value={newPhoto.category} onValueChange={(v) => setNewPhoto({ ...newPhoto, category: v })}>
                  <SelectTrigger className="bg-[#171717] border-[#262626]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex gap-3 pt-2">
                <Button variant="outline" className="flex-1 border-[#262626]" onClick={() => setShowUpload(false)}>
                  Cancel
                </Button>
                <Button 
                  className="flex-1 bg-[#FF4500] hover:bg-[#E65100]" 
                  onClick={handleUpload} 
                  disabled={uploading || (uploadMode === "file" && !cloudinaryConfigured)}
                  data-testid="upload-photo-btn"
                >
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Upload"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox */}
      {selectedPhoto && (
        <div className="fixed inset-0 bg-black/95 flex items-center justify-center z-50 p-4" onClick={() => setSelectedPhoto(null)}>
          <div className="relative max-w-4xl max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
            <img 
              src={selectedPhoto.image_url} 
              alt={selectedPhoto.caption} 
              className="max-w-full max-h-[80vh] object-contain rounded"
            />
            <div className="mt-4 flex items-center justify-between">
              <div>
                <span className="text-xs bg-[#FF4500] px-2 py-1 rounded text-white">{selectedPhoto.category}</span>
                {selectedPhoto.caption && (
                  <p className="text-white mt-2">{selectedPhoto.caption}</p>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  {new Date(selectedPhoto.uploaded_at).toLocaleDateString()}
                </p>
              </div>
              <Button 
                variant="destructive" 
                size="sm"
                onClick={() => handleDelete(selectedPhoto.photo_id)}
              >
                <Trash2 className="w-4 h-4 mr-1" />
                Delete
              </Button>
            </div>
            <button 
              className="absolute -top-2 -right-2 bg-white/10 rounded-full p-2 hover:bg-white/20"
              onClick={() => setSelectedPhoto(null)}
            >
              <X className="w-5 h-5 text-white" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
